import { ChevronLeft, ChevronRight } from 'lucide-react';
import styled from 'styled-components';

interface PaginationProps {
  page: number;
  totalPages: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  pageSizeOptions?: number[];
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-wrap: wrap;
`;

const Pages = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : 'transparent'};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.bgMuted : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  font-size: 13px;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgMuted};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ArrowButton = styled(PageButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PageSize = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  select {
    appearance: none;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: inherit;
    border-radius: ${({ theme }) => theme.radius.sm};
    padding: 4px 24px 4px 8px;
    font-size: 13px;
    cursor: pointer;
  }
`;

const Total = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Ellipsis = styled.span`
  padding: 0 6px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const buildPages = (current: number, total: number): (number | 'gap')[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('gap');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('gap');
  pages.push(total);
  return pages;
};

export const Pagination = ({
  page,
  totalPages,
  limit,
  total,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [10, 20, 50],
}: PaginationProps) => {
  const safeTotal = Math.max(totalPages, 1);
  const pages = buildPages(page, safeTotal);

  return (
    <Wrapper aria-label="Paginação">
      <Total>
        {total} {total === 1 ? 'resultado' : 'resultados'}
      </Total>

      <Pages>
        <ArrowButton
          aria-label="Página anterior"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft size={14} />
        </ArrowButton>

        {pages.map((p, i) =>
          p === 'gap' ? (
            <Ellipsis key={`gap-${i}`}>…</Ellipsis>
          ) : (
            <PageButton
              key={p}
              $active={p === page}
              aria-current={p === page ? 'page' : undefined}
              onClick={() => onPageChange(p)}
            >
              {p}
            </PageButton>
          ),
        )}

        <ArrowButton
          aria-label="Próxima página"
          disabled={page >= safeTotal}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight size={14} />
        </ArrowButton>
      </Pages>

      {onLimitChange && (
        <PageSize>
          <label htmlFor="page-size">Por página</label>
          <select
            id="page-size"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </PageSize>
      )}
    </Wrapper>
  );
};
